import { useState, useEffect, useRef } from 'react';
import { OnboardingShell } from './OnboardingShell';
import { IdentitySetup } from './IdentitySetup';
import { OrganizationSetup } from './OrganizationSetup';
import { AiCalibration } from './AiCalibration';
import { KnowledgeDeposit } from './KnowledgeDeposit';
import { FirstMission } from './FirstMission';
import { TeamMemberWelcome } from './TeamMemberWelcome';
import type { OnboardingFlowType } from '@/hooks/useOnboardingState';

type FullOnboardingStep = 'identity' | 'organization' | 'ai-calibration' | 'knowledge' | 'mission';
type InvitedOnboardingStep = 'identity' | 'welcome';
type Direction = 'forward' | 'back';

interface OnboardingFlowProps {
  onComplete: (projectId?: string) => void;
  flowType: OnboardingFlowType;
}

const FULL_STEP_ORDER: FullOnboardingStep[] = ['identity', 'organization', 'ai-calibration', 'knowledge', 'mission'];
const INVITED_STEP_ORDER: InvitedOnboardingStep[] = ['identity', 'welcome'];

interface ShellMeta {
  step: number;
  totalSteps: number;
  stepLabel: string;
  maxWidth?: string;
}

const FULL_SHELL_META: Record<FullOnboardingStep, ShellMeta> = {
  identity: { step: 1, totalSteps: 5, stepLabel: 'Identity' },
  organization: { step: 2, totalSteps: 5, stepLabel: 'Organization' },
  'ai-calibration': { step: 3, totalSteps: 5, stepLabel: 'AI Calibration', maxWidth: '520px' },
  knowledge: { step: 4, totalSteps: 5, stepLabel: 'Knowledge Deposit' },
  mission: { step: 5, totalSteps: 5, stepLabel: 'First Proposal' },
};

const INVITED_SHELL_META: Record<InvitedOnboardingStep, ShellMeta> = {
  identity: { step: 1, totalSteps: 2, stepLabel: 'Identity' },
  welcome: { step: 2, totalSteps: 2, stepLabel: 'Welcome' },
};

export function OnboardingFlow({ onComplete, flowType }: OnboardingFlowProps) {
  const [fullStep, setFullStep] = useState<FullOnboardingStep>('identity');
  const [invitedStep, setInvitedStep] = useState<InvitedOnboardingStep>('identity');
  const [, setExtractedCount] = useState(0);
  const [direction, setDirection] = useState<Direction>('forward');
  const transitionKeyRef = useRef(0);

  // Bump key on every step change so the animation re-runs
  useEffect(() => {
    transitionKeyRef.current += 1;
  }, [fullStep, invitedStep]);

  const advance = (apply: () => void) => {
    setDirection('forward');
    apply();
  };

  const retreat = (apply: () => void) => {
    setDirection('back');
    apply();
  };

  // Forward handlers — full flow
  const handleIdentityComplete = () => {
    if (flowType === 'invited') {
      advance(() => setInvitedStep('welcome'));
    } else {
      advance(() => setFullStep('organization'));
    }
  };
  const handleOrganizationComplete = () => advance(() => setFullStep('ai-calibration'));
  const handleAiCalibrationComplete = () => advance(() => setFullStep('knowledge'));
  const handleAiCalibrationSkip = () => advance(() => setFullStep('knowledge'));
  const handleKnowledgeComplete = (count: number) => {
    setExtractedCount(count);
    advance(() => setFullStep('mission'));
  };
  const handleKnowledgeSkip = () => advance(() => setFullStep('mission'));
  const handleMissionComplete = (projectId: string) => onComplete(projectId);
  const handleMissionSkip = () => onComplete();

  // Back handlers
  const handleFullBack = () => {
    const idx = FULL_STEP_ORDER.indexOf(fullStep);
    if (idx > 0) retreat(() => setFullStep(FULL_STEP_ORDER[idx - 1]));
  };
  const handleInvitedBack = () => {
    const idx = INVITED_STEP_ORDER.indexOf(invitedStep);
    if (idx > 0) retreat(() => setInvitedStep(INVITED_STEP_ORDER[idx - 1]));
  };

  // Invited completion
  const handleWelcomeComplete = () => onComplete();

  // Resolve current shell meta + content
  const isInvited = flowType === 'invited';
  const meta = isInvited ? INVITED_SHELL_META[invitedStep] : FULL_SHELL_META[fullStep];
  const canGoBack = isInvited
    ? INVITED_STEP_ORDER.indexOf(invitedStep) > 0
    : FULL_STEP_ORDER.indexOf(fullStep) > 0;
  const handleBack = isInvited ? handleInvitedBack : handleFullBack;
  const contentKey = isInvited ? `invited-${invitedStep}` : `full-${fullStep}`;

  const renderContent = () => {
    if (isInvited) {
      if (invitedStep === 'identity') {
        return <IdentitySetup onComplete={handleIdentityComplete} totalSteps={2} />;
      }
      return <TeamMemberWelcome onComplete={handleWelcomeComplete} />;
    }

    switch (fullStep) {
      case 'identity':
        return <IdentitySetup onComplete={handleIdentityComplete} />;
      case 'organization':
        return <OrganizationSetup onComplete={handleOrganizationComplete} />;
      case 'ai-calibration':
        return (
          <AiCalibration
            onComplete={handleAiCalibrationComplete}
            onSkip={handleAiCalibrationSkip}
          />
        );
      case 'knowledge':
        return (
          <KnowledgeDeposit
            onComplete={handleKnowledgeComplete}
            onSkip={handleKnowledgeSkip}
          />
        );
      case 'mission':
        return (
          <FirstMission
            onComplete={handleMissionComplete}
            onSkip={handleMissionSkip}
          />
        );
    }
  };

  return (
    <OnboardingShell
      step={meta.step}
      totalSteps={meta.totalSteps}
      stepLabel={meta.stepLabel}
      maxWidth={meta.maxWidth}
      onBack={canGoBack ? handleBack : undefined}
    >
      <div
        key={contentKey}
        className={direction === 'forward' ? 'onboarding-slide-forward' : 'onboarding-slide-back'}
      >
        {renderContent()}
      </div>
    </OnboardingShell>
  );
}
